import { CURSOR_OVER } from 'game/constants/Constants.js';

const normalise = (val, min, max, rmin, rmax) => {
  const n = ((val - rmin) / (rmax - rmin)) * (max - min) + min;
  return Math.min(Math.max(n, min), max);
};

export default class MagnifyGlass {
  constructor(scene, x, y) {
    this.scene = scene;
    this.glass = scene.add.image(x, y, 'shared_assets', 'magnifying-glass').setOrigin(0.36, 0.33);
    this.isDown = false;
    this.isDragging = false;
    this.events = new Phaser.Events.EventEmitter();
    this.particles = scene.add.particles(x, y, 'toybox', {
      lifespan: { min: 1000, max: 2000 },
      scale: { start: 1, end: 0 },
      frequency: 0,
      quantity: 0,
      // maxParticles: 50,
      speedX: { min: -20, max: 20 },
      speedY: { min: -20, max: 20 },
      emitZone: {
        type: 'random',
        source: new Phaser.Geom.Circle(0, 0, 150),
      },
      active: false,
      frame: [
        'toyboox_star_1',
        'toyboox_star_2',
        'toyboox_star_3',
        'toyboox_star_4',
        'toyboox_star_5',
        'toyboox_star_6',
      ],
    });
  }

  get x() {
    return this.glass.x;
  }

  get y() {
    return this.glass.y;
  }

  set scaleMin(val) {
    this._scaleMin = val;
  }

  bounceIn() {
    this.glass.alpha = 0;
    this.scene.tweens.add({
      targets: this.glass,
      alpha: { from: 0.2, to: 1 },
      delay: 300,
      duration: 500,
    });
    this.scene.tweens.chain({
      tweens: [
        {
          targets: this.glass,
          y: { from: this.glass.y - 150, to: this.glass.y },
          scale: { from: 0, to: this._scaleMin },
          duration: 800,
          ease: Phaser.Math.Easing.Cubic.Out,
        },
      ],
    });
  }

  bouncePrompt(delay = 2000) {
    this.scene.tweens.add({
      targets: this.glass,
      scale: this._scaleMin + 0.2,
      delay,
      yoyo: true,
      repeat: 1,
      duration: 150,
    });
  }

  setScale(val) {
    this.glass.scale = val;
    const n = normalise(val, 0, 1, 0.6, 1.2);
    const step = Math.ceil((n * 40) / 10) + 1;

    const showParticles = val > 0.6;
    const f = showParticles ? (8 - this.step) * 50 : 0;
    const q = showParticles ? step : 0;

    if (step !== this.step) {
      this.step = step;
      this.particles.setQuantity(q);
      this.particles.frequency = f;
    }
  }

  startPrompt(d = 10, voId, trackStop) {
    if (trackStop) {
      this.scene.game.trackPromptStop();
    }
    this.endPrompt();
    this.promptTimer = this.scene.time.addEvent({
      delay: d * 1000,
      callback: () => {
        this.scene.game.trackPromptStart();
        this.scene?.game?.vo?.play(voId);
        this.startPrompt(10, voId);
        this.bouncePrompt();
      },
    });
  }

  endPrompt() {
    if (this.promptTimer) {
      this.promptTimer.remove();
      this.promptTimer = null;
    }
  }

  activate() {
    this.glass.setInteractive({ cursor: CURSOR_OVER, draggable: false });
    this.glass.once('pointerdown', this.handleInputDown, this);
    this.scene.input.on('pointerup', this.handleInputUp, this);
    this.startPrompt(3, 'NFC_2.0_34', true);
  }

  handleInputDown(pointer) {
    this.scene.game.soundController.playSFX('sfx', 'sfx_mag-pickup');
    this.startPrompt(5, 'NFC_2.0_35', true);
    this.isDragging = true;
    this.scene.input.on('pointermove', this.handleDrag, this);
    this.isDown = true;
    this.glass.setPosition(pointer.x, pointer.y);
    this.particles.active = true;
  }

  handleInputUp() {
    if (this.isDown) {
      this.pickUp();
    } else {
      this.drop();
      this.startPrompt(5, 'NFC_2.0_34', true);
    }
  }

  pickUp() {
    this.isDown = false;
    this.particles.start();
  }

  drop() {
    this.isDragging = false;
    this.particles.stop();
    this.scene.input.off('pointermove', this.handleDrag);
    this.glass.once('pointerdown', this.handleInputDown, this);
    this.scene.tweens.add({
      targets: this.glass,
      scale: { from: this.glass.scale, to: this._scaleMin },
      duration: 80,
      ease: Phaser.Math.Easing.Cubic.In,
    });
  }

  handleDrag(pointer, dragX, dragY) {
    if (this.isDragging) {
      this.glass.setPosition(pointer.x, pointer.y);
      this.particles.setPosition(pointer.x, pointer.y);
    }
  }

  deActivate() {
    this.endPrompt();
    if (this.glass) {
      this.isDragging = false;
      this.scene.input.off('pointerup', this.handleInputUp);
      this.scene.input.off('pointermove', this.handleDrag);
      this.scene.input.off('pointerdown', this.handleInputDown);
    }
  }

  handleHotspotTrigger({ wx, wy, id, spine, animation, sfx }, isTalking = true) {
    this.deActivate();
    this.scene.game.trackPromptStop();
    this.glass.removeInteractive();
    this.particles.visible = false;
    this.scene.game.soundController.playSFX('sfx', 'sfx_mag-zoom');
    if (spine && animation) {
      if (!this.spine) {
        this.spine = this.scene.add.spine(0, 0, spine, animation, true);
      } else {
        this.spine.play(animation, false, true);
      }
      this.hotspotSFX = false;
      if (sfx)
        this.hotspotSFX = this.scene.game.soundController.playSFX(sfx.sprite, sfx.id, { ...sfx });
      this.spine.alpha = 0;
      this.setTalking(isTalking);
      this.spine.setToSetupPose();
      this.spine.x = wx;
      this.spine.y = wy;
      this.ring = this.scene.add.image(wx, wy, 'shared', 'magnifying-glass-frame');
      this.scene.tweens.add({
        targets: this.spine,
        alpha: 1,
        duration: 300,
      });
    }
    this.scene.tweens.add({
      targets: this.glass,
      alpha: 0,
      duration: 200,
    });
  }

  setTalking(value) {
    if (!this.spine) return;
    if (value && this.spine.findSkin('lipflap')) {
      this.spine.setSkinByName('lipflap');
    } else if (!value && this.spine.findSkin('normal')) {
      this.spine.setSkinByName('normal');
    }
  }

  remove() {
    if (this.hotspotSFX) {
      this.scene.game.soundController.stopSFX(this.hotspotSFX);
      this.hotspotSFX = null;
    }
    this.endPrompt();
    const targets = [];
    this.spine && targets.push(this.spine);
    this.ring && targets.push(this.ring);
    if (targets.length > 0) {
      this.scene.tweens.add({
        targets: targets,
        alpha: 0,
        duration: 200,
        onComplete: () => {
          this.events.emit('complete');
          if (this.ring) {
            this.ring.destroy();
            this.ring = null;
          }
        },
      });
    } else {
      this.events.emit('complete');
    }
  }

  destroy() {
    if (this.hotspotSFX) {
      this.scene.game.soundController.stopSFX(this.hotspotSFX);
      this.hotspotSFX = null;
    }
    if (this.scene) {
      this.scene?.game?.trackPromptStop();
    }
    this.deActivate();
    if (this.events) {
      this.events?.shutdown();
      this.events = null;
    }
    if (this.ring) {
      this.ring.destroy();
      this.ring = null;
    }
    if (this.particles) {
      this.particles.stop();
      this.particles.destroy();
      this.particles = null;
    }
    if (this.glass) {
      this.scene.tweens.killTweensOf(this.glass);
      this.glass.destroy();
      this.glass = null;
    }
    if (this.spine) {
      this.spine.destroy();
      this.spine = null;
    }
    this.scene = null;
  }
}
